import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import BcaApis from "src/api/bca";
import { MessageCircle, ChevronUp } from "lucide-react";
import supportPrograms from "src/data/app/programs";

export interface BcaLogItem {
  created_at?: string;
  weight?: number | string;
  bca_sync_logs_id?: string | number;
  synced_device?: string;
  body_fat_percentage?: number | string;
  muscle_mass?: number | string;
  bmi?: number | string;
  [key: string]: unknown;
}

const BCAShowAllDataScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const navigate = useNavigate();
  const [logs, setLogs] = useState<BcaLogItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const limit = 10;

  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const bottomBarItems =
    config?.app?.screens && typeof config.app.screens === "object"
      ? Object.keys(config.app.screens).map((key) => {
          const screen = (config.app.screens as Record<string, any>)[key];
          return {
            screenName: screen?.title,
            icon: screen?.icon,
            activeIcon: screen?.activeIcon,
            screenKey: screen?.id,
          };
        })
      : [];

  const fetchLogs = useCallback(async (pageNum: number, append: boolean) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const res: any = await BcaApis.getBcaLogs({
        queryParams: { page: pageNum, limit: String(limit) },
      });
      const code = res?.code != null ? Number(res.code) : null;
      if (code === -1 || code === 0) {
        setLogs((prev) => (pageNum === 1 ? [] : prev));
        return;
      }
      const list = res?.data?.bca_logs ?? [];
      const total = res?.data?.total_records ?? 0;
      setTotalRecords(total);
      setLogs((prev) => (append ? [...prev, ...list] : list));
    } catch {
      setLogs((prev) => (pageNum === 1 ? [] : prev));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1, false);
  }, [fetchLogs]);

  const handleBack = () => {
    navigate(
      `/app/${supportProgramKey}/insights-screen/vitals/body-composition`
    );
  };

  const handleLoadMore = () => {
    if (loadingMore || logs.length >= totalRecords) return;
    setPage((p) => p + 1);
  };

  useEffect(() => {
    if (page > 1) fetchLogs(page, true);
  }, [page, fetchLogs]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return { date: "—", time: "—" };
    const m = moment(dateString, "YYYY-MM-DD HH:mm:ss", true);
    if (!m.isValid()) return { date: dateString, time: "" };
    return {
      date: m.format("ddd, MMM D"),
      time: m.format("h:mm A"),
    };
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey}
      // bottomBarItems={bottomBarItems}
      header={
        <Header
          title="Show All Data"
          showBackButton
          onBackClick={handleBack}
          titlePosition="center"
        />
      }
    >
      <div className="px-4 py-4 pb-24 max-w-lg mx-auto">
        <p className="text-[var(--text-secondary)] text-sm mb-6">
          Showing detailed logs from health assistant action flows with user
          inputs and calculated outputs.
        </p>

        {loading && logs.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-2 border-[var(--app-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-[var(--text-secondary)]">
            No logs available
          </div>
        ) : (
          <div className="space-y-4">
            {logs.map((log, index) => {
              const { date, time } = formatDate(log?.created_at);
              const weightVal =
                log?.weight != null ? Number(log.weight).toFixed(1) : "—";
              const weightDisplay =
                log?.weight != null ? `${weightVal} kg` : "—";
              return (
                <div
                  key={log.bca_sync_logs_id ?? index}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
                >
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {date}
                      </div>
                      <div className="text-xs text-gray-500">{time}</div>
                    </div>
                    <div className="text-sm font-semibold text-gray-800">
                      {weightDisplay}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="font-medium text-gray-700">
                      User Inputs Selected:
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Entry Method:</span>
                      <span>{log.synced_device ?? "Device"}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Value:</span>
                      <span>{weightVal}</span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="font-medium text-gray-700">
                      Calculated Results:
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
                        <div className="text-xs text-gray-500 uppercase">
                          STATUS
                        </div>
                        <div className="font-semibold text-gray-800">
                          {log.bmi != null ? "Normal" : "Synced"}
                        </div>
                      </div>
                      <div className="flex-1 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
                        <div className="text-xs text-gray-500 uppercase">
                          TREND
                        </div>
                        <div className="font-semibold text-gray-800">
                          Stable
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-green-600">
                    Data logged successfully
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && logs.length > 0 && logs.length < totalRecords && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="py-2 px-4 rounded-xl border border-gray-300 text-sm font-medium disabled:opacity-50"
            >
              {loadingMore ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BCAShowAllDataScreen;
